import React from 'react';

/* ── Urgency helpers ── */

const parseDate = (raw) => {
  if (!raw) return null;
  const d = typeof raw === 'string'
    ? (raw.includes('T') ? new Date(raw) : new Date(`${raw}T00:00:00`))
    : new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

/** Compute urgency level for the top-bar accent color */
function getUrgencyLevel(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return 'muted';
  const diffMs = d.getTime() - Date.now();
  const diffD = diffMs / 86400000;
  if (diffMs < 0) return 'danger';
  if (diffD < 1) return 'warning';
  if (diffD < 2) return 'info';
  return 'info';
}

/** Short human date: "Tue Mar 10 at 9:00 AM" */
function formatShortDateTime(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Relative label: "in 8 days", "tomorrow", "3d overdue" */
function formatRelative(dateStr) {
  const d = parseDate(dateStr);
  if (!d) return '';
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffD = diffMs / 86400000;
  if (diffMs < 0) {
    const overdueDays = Math.abs(Math.floor(diffD));
    return overdueDays < 1 ? 'earlier today' : `${overdueDays}d overdue`;
  }
  if (diffD < 1) {
    const hrs = Math.floor(diffMs / 3600000);
    return hrs < 1 ? 'less than 1 hr' : `in ${hrs} hr${hrs !== 1 ? 's' : ''}`;
  }
  if (diffD < 2) return 'tomorrow';
  return `in ${Math.ceil(diffD)} days`;
}

/* ── Session-based hero card ── */

function NextActionCard({ hero, isHeroExpanded, onFocusSession, onComplete, onReviewTasks, completingId }) {
  if (!hero) return null;

  const { group: session, incomplete, earliestDue } = hero;
  const incompleteCount = incomplete.length;
  const dueDate = earliestDue?.date || session.date;
  const urgencyLevel = getUrgencyLevel(dueDate);
  const isCompleting = completingId === '__hero_completing__';

  return (
    <div className={`next-action-card next-action--${urgencyLevel}`}>
      {/* Left: content */}
      <div className="nac-content">
        <div className="nac-top-row">
          <div className="next-action-badge">
            <span className="next-action-badge-dot" />
            Next Up
          </div>
        </div>
        <h2 className="next-action-title">{session.title}</h2>
        <p className="nac-subline">
          {incompleteCount === 0 ? (
            <span className="nac-tasks">No tasks remaining</span>
          ) : (
            <>
              <span className="nac-tasks">
                {incompleteCount} task{incompleteCount !== 1 ? 's' : ''} to complete
              </span>
              {dueDate && (
                <span className="nac-due">
                  Due {formatShortDateTime(dueDate)} · {formatRelative(dueDate)}
                </span>
              )}
            </>
          )}
        </p>
      </div>

      {/* Right: stacked actions */}
      <div className="nac-actions">
        {incompleteCount === 1 && (
          <button
            className="next-action-btn next-action-btn--primary"
            onClick={onComplete}
            disabled={isCompleting}
          >
            {isCompleting ? 'Updating…' : 'Mark Complete'}
          </button>
        )}
        {incompleteCount > 1 && (
          <button
            className="next-action-btn next-action-btn--primary"
            onClick={onReviewTasks}
            disabled={isCompleting}
          >
            {isCompleting ? 'Updating…' : `Review ${incompleteCount} Tasks`}
          </button>
        )}
        {!isHeroExpanded && (
          <button
            className="next-action-btn next-action-btn--secondary"
            onClick={onFocusSession}
          >
            Jump to Session
          </button>
        )}
      </div>
    </div>
  );
}

export default React.memo(NextActionCard);
